import type {
  SpoilerPreferencesResponseDto,
  UpdateSpoilerPreferencesDto,
} from '@nomiss/sdk';
import {
  ApiRequestError,
  executeSdkRawRequest,
} from './api-request.util';
import { spoilerShieldApi } from './sdk-client';

export type SpoilerSportPreference = {
  sportId: string;
  sportCode: string;
  sportName: string;
  isEnabled: boolean;
};

export type SpoilerTeamPreference = {
  teamId: string;
  teamName: string;
  isEnabled: boolean;
};

export type SpoilerTeamsGroup = {
  sportId: string;
  sportName: string;
  items: SpoilerTeamPreference[];
};

export type SpoilerPreferencesResponse = {
  tier: string;
  globalEnabled: boolean;
  sports: SpoilerSportPreference[];
  teams: SpoilerTeamsGroup[];
};

export type UpdateSpoilerPreferencesInput = {
  global?: { isEnabled: boolean };
  sports?: Array<{ sportId: string; isEnabled: boolean }>;
  teams?: Array<{ teamId: string; isEnabled: boolean }>;
};

export class SpoilerShieldError extends ApiRequestError {
  constructor(status: number, message: string) {
    super(status, message, 'SpoilerShieldError');
  }
}

function normalizeSpoilerPreferences(
  payload: SpoilerPreferencesResponseDto,
): SpoilerPreferencesResponse {
  const globalRecord = payload.global as Record<string, unknown>;

  return {
    tier: payload.tier,
    // Global shield is on by default; only an explicit `false` turns it off.
    globalEnabled: globalRecord.isEnabled !== false,
    sports: (payload.sports ?? []).map(sport => ({
      sportId: sport.sportId,
      sportCode: sport.sportCode,
      sportName: sport.sportName,
      isEnabled: sport.isEnabled,
    })),
    teams: (payload.teams ?? []).map(group => ({
      sportId: group.sportId,
      sportName: group.sportName,
      items: (group.items ?? []).map(item => ({
        teamId: item.teamId,
        teamName: item.teamName,
        isEnabled: item.isEnabled,
      })),
    })),
  };
}

export async function getSpoilerPreferences(): Promise<SpoilerPreferencesResponse> {
  const data = await executeSdkRawRequest<SpoilerPreferencesResponseDto>({
    request: () => spoilerShieldApi.spoilersControllerGetV1Raw({}),
    fallback: 'Failed to load spoiler preferences',
    ErrorClass: SpoilerShieldError,
  });

  return normalizeSpoilerPreferences(data);
}

export async function updateSpoilerPreferences(
  input: UpdateSpoilerPreferencesInput,
): Promise<SpoilerPreferencesResponse> {
  const updateDto: UpdateSpoilerPreferencesDto = {
    ...(input.global !== undefined
      ? { global: { isEnabled: input.global.isEnabled } }
      : {}),
    ...(input.sports !== undefined ? { sports: input.sports } : {}),
    ...(input.teams !== undefined ? { teams: input.teams } : {}),
  };

  const data = await executeSdkRawRequest<SpoilerPreferencesResponseDto>({
    request: () =>
      spoilerShieldApi.spoilersControllerUpdateV1Raw({
        updateSpoilerPreferencesDto: updateDto,
      }),
    fallback: 'Failed to update spoiler preferences',
    ErrorClass: SpoilerShieldError,
  });

  return normalizeSpoilerPreferences(data);
}
